import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api.js';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog.jsx';
import { formatHumanDate } from '@/lib/date-display.js';
import useMe from '@/hooks/useMe.js';
import { ownerPagesVisible } from '@/lib/session.js';

// AUTOMATIONS, ONE SWITCH EACH (Q61, David's ruling of 2026-10-22: "Q61 yes (TRACON switch, default
// off)"; PLAN-ROUND6 section 6, task S18).
//
// "Switches should exist in TRACON not in Render" (CLAUDE.md Automation Control), and a door ships
// with its handle: this is the handle of admin_settings phf.primary_nudges.mode, read by ONE reader,
// services/primary-nudge-switch.service.js primaryNudgeMode. Off | Auto send, DEFAULT OFF: no row, an
// unreadable row or any other value reads off. The Render env PHF_PRIMARY_NUDGES_OFF is the emergency
// override and forces off; the server's answer says so (why env).
// The PATCH door (routes/admin-settings.routes.js validatePrimaryNudgeModeWrite) refuses any other
// value and refuses deleting the row; migration people-create-door-03 writes it at "off".
//
// The page writes through PATCH /api/tracon/admin/admin-settings (the broker-token mount; the key-only
// /api/admin mount authorises nothing through the Pages proxy) and then READS the switch back through its
// one reader (GET /api/tracon/admin/admin-settings/switch/:key; a failed read throws, so it never looks like
// Off) before it says anything: a confirmation is a read, not an echo. The page derives nothing.
//
// NOT under this switch: the manual 'primary-pilot-nudge' template in routes/tracon-send.routes.js
// (SendToClientSheet). That is David's own hand, one send at a time.
//
// deals-door-1 T25 (David's brief of 2026-10-27, item 5; PLAN-FINAL 7.2): the second switch is the
// nightly expiry job's, admin_settings deal.expiry.mode, read by ONE reader,
// services/deal-expiry-switch.service.js dealExpiryMode. Off | List only (surface) | Expire (write);
// migration deals-door-1-01 writes it at "surface"; no row, an unreadable row or any other value reads
// off; the Render env DEAL_EXPIRY_OFF forces off. Owner only: the row shows only when /auth/me says
// owner (ownerPagesVisible); the server's PATCH gate (admin_settings privilege) is the real gate.
// deals-door-1 T26 (PLAN-FINAL 7.2, 7.3 step 9): the expiry row also shows the job's standing list, counts by reason, from
// GET /api/tracon/deal-expiry/standing (read-only, no Zoho); the reasons' words are the server's, the page derives nothing,
// and a failed read says so (never an empty list).
//
// paperwork-1 L1a-T4 (PLAN D9, D12, D13, A13; screen 8): three more switches, each read by ONE reader built on
// services/admin-mode-switch.js, and migration paperwork-1-01 writes every row. Owner only, as the expiry row.
//   paperwork.endorsement_autosend.mode  services/endorsement-autosend-switch.service.js  Off | List only | Auto send
//                                        (launch: List only); DOCUMENT_AUTOSEND_OFF forces Off
//   paperwork.morning_report.mode        services/morning-report-switch.service.js        Off | On (launch: Off);
//                                        MORNING_REPORT_OFF forces Off
//   paperwork.payment_holds.mode         services/payment-holds-switch.service.js         Notify | Hold (launch: Notify);
//                                        an unreadable row reads Notify, never Hold; PAYMENT_HOLDS_OFF forces Notify
// main batch 2026-11-01: numbering.copy_back.mode (services/number-copy-back-switch.service.js, Off | On, launch: On, a data copy
// into TRACON that sends nothing; NUMBER_COPY_BACK_OFF forces Off), written by migration main-batch-2026-11-01-n4. Owner only.
// And one AGENCY SETTING, paperwork.signed_application_years (services/signed-application-window.service.js, an integer 1 to
// 5, launch 1): a number input, written through the same PATCH and read back through the same switch door (its answer is
// { value, why }) before the page says anything.
//
// Each switch's key and modes match KEY and MODES in its reader, and the setting's range its reader's MIN_YEARS and
// MAX_YEARS; the guards tests/automations-admin-page.test.js, tests/deal-expiry-switch.test.js and
// tests/paperwork-switches.test.js hold them equal.

const SWITCHES = [
  {
    key: 'phf.primary_nudges.mode',
    label: 'Primary pilot-form nudge',
    what: 'The scheduled email to the account primary listing the pilots whose forms are still outstanding.',
    note: 'Off sends nothing. The Render setting PHF_PRIMARY_NUDGES_OFF forces Off.',
    envName: 'PHF_PRIMARY_NUDGES_OFF',
    modes: [
      { mode: 'off', label: 'Off', note: 'Nothing is sent. A dry run still lists who would get it.' },
      { mode: 'auto_send', label: 'Auto send', note: 'The scheduled run sends the nudge.' },
    ],
  },
  {
    key: 'deal.expiry.mode',
    label: 'Expire ended policies nightly',
    what: 'Each night, the policies whose term ended and that still read Bound or Paid in Zoho are moved to Expired, as the Zoho workflow does today. Only you get the mail.',
    note: 'Off does nothing. List only mails you what would expire and changes nothing. The Render setting DEAL_EXPIRY_OFF forces Off.',
    envName: 'DEAL_EXPIRY_OFF',
    owner: true,
    standing: '/api/tracon/deal-expiry/standing',
    modes: [
      { mode: 'off', label: 'Off', note: 'The nightly run does nothing.' },
      { mode: 'surface', label: 'List only', note: 'The nightly run lists and mails what would expire, and changes nothing.' },
      { mode: 'write', label: 'Expire', note: 'The nightly run moves each ended policy to Expired and mails what it did.' },
    ],
  },
  {
    key: 'paperwork.endorsement_autosend.mode',
    label: 'Send paid endorsements to the client',
    what: 'When Nickel confirms an endorsement payment, the carrier\'s endorsement is emailed to the client, attached, and logged on the deal.',
    note: 'Off sends nothing. List only records what would have gone, on the deal and in the morning report, and sends nothing. The Render setting DOCUMENT_AUTOSEND_OFF forces Off.',
    envName: 'DOCUMENT_AUTOSEND_OFF',
    owner: true,
    standing: '/api/tracon/paperwork/autosend-standing',
    modes: [
      { mode: 'off', label: 'Off', note: 'Nothing is sent and nothing is listed.' },
      { mode: 'surface', label: 'List only', note: 'Each paid endorsement is listed as would send; nothing is emailed.' },
      { mode: 'auto_send', label: 'Auto send', note: 'Each paid endorsement is emailed to the client once, logged. Only a Nickel payment notice TRACON could verify sends; one it could not verify is listed instead.' },
    ],
  },
  {
    key: 'paperwork.morning_report.mode',
    label: 'Morning report',
    what: 'Once each weekday morning, one email to you: policies with no policy document filed, endorsements that would have been sent, and payment requests sent without their paperwork. Never twice in a day and never on a weekend (Monday\'s report carries the weekend). No email on a day with nothing to say.',
    note: 'Off sends nothing. The Render setting MORNING_REPORT_OFF forces Off.',
    envName: 'MORNING_REPORT_OFF',
    owner: true,
    standing: '/api/tracon/paperwork/missing-policy-standing',
    modes: [
      { mode: 'off', label: 'Off', note: 'No report is sent.' },
      { mode: 'on', label: 'On', note: 'The report is emailed once each weekday it has something to say.' },
    ],
  },
  {
    key: 'paperwork.payment_holds.mode',
    label: 'Hold payment requests missing their paperwork',
    what: 'A bind or renewal payment request with no signed application in the window, an endorsement payment request before the carrier\'s endorsement is filed, and Signed paperwork received without the application.',
    note: 'Notify lets each one go and flags it on the deal and in the morning report. Hold stops it and says why; a waiver on the deal still lets it go. A switch that cannot be read reads Notify, never Hold. The Render setting PAYMENT_HOLDS_OFF forces Notify.',
    envName: 'PAYMENT_HOLDS_OFF',
    owner: true,
    standing: '/api/tracon/paperwork/holds-standing',
    modes: [
      { mode: 'notify', label: 'Notify', note: 'The request goes; the deal and the morning report are flagged.' },
      { mode: 'hold', label: 'Hold', note: 'The request is stopped until the paperwork is filed or the deal carries a waiver.' },
    ],
  },
  {
    key: 'numbering.copy_back.mode',
    label: 'Copy the account and application numbers from Zoho nightly',
    what: 'Each night, an account or policy that Zoho has numbered but TRACON has not gets Zoho\'s number copied in. Each new account and deal already copies its number right after it is created; this catches any that missed. It never replaces a number TRACON holds, never writes to Zoho and sends no email.',
    note: 'Off copies nothing at night (the copy right after each create still runs). It only runs while Zoho issues the numbers. The Render setting NUMBER_COPY_BACK_OFF forces Off.',
    envName: 'NUMBER_COPY_BACK_OFF',
    owner: true,
    modes: [
      { mode: 'off', label: 'Off', note: 'The nightly run copies nothing.' },
      { mode: 'on', label: 'On', note: 'The nightly run copies each number Zoho holds and TRACON does not.' },
    ],
  },
];
const labelOf = (s, mode) => (s.modes.find((m) => m.mode === mode) || s.modes[0]).label;

// AGENCY SETTINGS: a number each, read through the same switch door ({ value, why }); the page derives nothing.
const SETTINGS = [
  {
    key: 'paperwork.signed_application_years',
    label: 'Signed application required every',
    unit: 'years',
    unitOne: 'year',
    what: 'A bind, a renewal payment request and Signed paperwork received need a signed application filed on this policy, or on a prior term signed within this many years.',
    min: 1,
    max: 5,
    owner: true,
  },
];

// The server's answer is the state (fix round, review R2-L3: one fact, one derivation). Each switch is
// read through GET /api/tracon/admin/admin-settings/switch/:key, the answer of its ONE reader ({ mode, why });
// the page derives nothing. These are the words for each why, labels only.
const WHY_WORDS = {
  row: '',
  absent: 'no row yet',
  invalid: 'the row holds a value the switch does not know',
  unreadable: 'the row could not be read',
  env: 'by the Render setting',
};
const stateFrom = (s, d) => ({ mode: d.mode, why: d.why === 'env' ? `forced ${labelOf(s, d.mode)} ${WHY_WORDS.env} ${s.envName}` : (WHY_WORDS[d.why] ?? `read ${d.why}`) });

// Throws when the read fails (never an answer that looks like Off).
async function readSwitches() {
  const out = {};
  for (const s of SWITCHES) {
    const d = await api.get(`/api/tracon/admin/admin-settings/switch/${encodeURIComponent(s.key)}?_t=${Date.now()}`);
    if (!d || !d.success || !s.modes.some((m) => m.mode === d.mode)) throw new Error('the switch read answered no mode');
    out[s.key] = stateFrom(s, d);
  }
  return out;
}

const unitFor = (s, n) => (n === 1 ? s.unitOne : s.unit);

// Throws when the read fails or answers no whole number (never a number the page made up).
async function readSettings() {
  const out = {};
  for (const s of SETTINGS) {
    const d = await api.get(`/api/tracon/admin/admin-settings/switch/${encodeURIComponent(s.key)}?_t=${Date.now()}`);
    if (!d || !d.success || !Number.isInteger(d.value)) throw new Error('the setting read answered no number');
    out[s.key] = { value: d.value, why: WHY_WORDS[d.why] ?? `read ${d.why}` };
  }
  return out;
}

// The job's standing list, counts by reason (the server's words, its heading too when it sends one: the paid-endorsement
// row's GET /api/tracon/paperwork/autosend-standing, paperwork-1 L3-3). A failed read says so, never "nothing standing".
// A reason's lines, when the server sends them (the paid-endorsement row does: each in its words with "Open <deal>").
//
// "NOT NEEDED" (mb-automations, David 2026-11-01: "every listed item (would-send endorsements, held payment requests,
// missing-document notices) gets 'Not needed' to dismiss it for good (reason optional, logged, undo), so the lists only show
// what still needs me"). A line the server marks dismissable (line.dismiss: { kind, key }, the item's own identity; the page
// derives nothing) carries a "Not needed" button: a dialog, the reason optional, POST /api/tracon/automations/dismissals
// (owner or producer, own sign-in; the one writer). The list then READS ITSELF BACK and says what it found (a confirmation is
// a read, not an echo). The server sets the dismissed aside under `dismissed`; the row shows "N dismissed", which opens them
// with who, when and why, each with "Undo Not needed" (POST /api/tracon/automations/dismissals/:id/undo, read back the same
// way). A dismissals read that failed is said (everything is listed). Nothing here sends, voids, deletes or releases.
const STANDING_LINES_SHOWN = 10;
const readStanding = async (url) => {
  const d = await api.get(`${url}?_t=${Date.now()}`);
  if (!d || !d.ok || !Array.isArray(d.reasons)) throw new Error('it answered no list');
  return d;
};
const sameItem = (a, b) => !!a && !!b && a.kind === b.kind && String(a.key) === String(b.key);
const dismissedHas = (d, item) => (Array.isArray(d.dismissed) ? d.dismissed : []).some((l) => sameItem(l.dismissal, item));
const listedHas = (d, item) => Object.values(d.lines || {}).some((ls) => Array.isArray(ls) && ls.some((l) => sameItem(l.dismiss, item)));
const onDay = (at) => formatHumanDate(at) || 'an unknown day';

function NotNeededDialog({ line, url, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  async function markNotNeeded() {
    setBusy(true);
    try {
      await api.post('/api/tracon/automations/dismissals', { kind: line.dismiss.kind, key: line.dismiss.key, reason: reason.trim() || null });
      let d;
      try { d = await readStanding(url); } catch {
        toast.info(`${line.title}: marked Not needed, but the list could not be read back. Reload to check it.`);
        onDone(null); return;
      }
      if (dismissedHas(d, line.dismiss) && !listedHas(d, line.dismiss)) toast.success(`${line.title}: marked Not needed. It is off this list and the morning report; Undo is under "dismissed".`);
      else toast.error(`${line.title}: saved, but the list read back still shows it. Reload to check it.`);
      onDone(d);
    } catch (e) {
      toast.error(`Not marked: ${e?.body?.error || e.message || 'the request failed'}`);
    } finally { setBusy(false); }
  }
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-not-needed-dialog>
        <DialogHeader>
          <DialogTitle className="pr-6">Not needed: {line.title}</DialogTitle>
          <DialogDescription>
            It leaves this list and the morning report for good. Nothing is sent, voided or deleted, and nothing about the
            record changes{line.dismiss.kind === 'payment_request' ? ' (the payment request already went; it is not released or held)' : ''}.
            Undo is under "dismissed" on this row.
          </DialogDescription>
        </DialogHeader>
        <label className="block text-[13px] text-text">Why, if you want to say (logged with your name)
          <textarea className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text" rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} data-not-needed-reason autoFocus />
        </label>
        <DialogFooter className="sm:flex-wrap">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="accent" onClick={markNotNeeded} disabled={busy} data-not-needed-confirm>{busy ? 'Saving…' : 'Mark not needed'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DismissedLines({ lines, url, onDone }) {
  const [busy, setBusy] = useState(null);
  async function undo(l) {
    setBusy(l.dismissal.id);
    try {
      await api.post(`/api/tracon/automations/dismissals/${encodeURIComponent(l.dismissal.id)}/undo`, {});
      let d;
      try { d = await readStanding(url); } catch {
        toast.info(`${l.title}: Not needed undone, but the list could not be read back. Reload to check it.`);
        onDone(null); return;
      }
      if (!dismissedHas(d, l.dismissal)) toast.success(`${l.title}: Not needed undone. It is listed again while it still needs you.`);
      else toast.error(`${l.title}: the undo was saved, but the list read back still shows it dismissed. Reload to check it.`);
      onDone(d);
    } catch (e) {
      toast.error(`Not undone: ${e?.body?.error || e.message || 'the request failed'}`);
    } finally { setBusy(null); }
  }
  return (
    <ul className="ml-4 mt-1 space-y-1" data-dismissed-lines>
      {lines.map((l) => (
        <li key={l.dismissal.id} className="flex flex-wrap items-baseline gap-x-2">
          <span>{l.title}</span>
          <span className="text-text-dim">Not needed, {l.dismissal.by || 'someone'} on {onDay(l.dismissal.at)}{l.dismissal.reason ? ` ("${l.dismissal.reason}")` : ''}</span>
          <button type="button" className="font-medium text-accent underline-offset-2 hover:underline disabled:opacity-50" disabled={busy === l.dismissal.id} onClick={() => undo(l)} data-undo-not-needed>
            {busy === l.dismissal.id ? 'Undoing…' : 'Undo Not needed'}
          </button>
        </li>
      ))}
    </ul>
  );
}

function StandingCounts({ url }) {
  const [st, setSt] = useState({ loading: true });
  const [seq, setSeq] = useState(0);
  const [asking, setAsking] = useState(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [all, setAll] = useState({});
  useEffect(() => {
    let live = true;
    api.get(`${url}?_t=${Date.now()}`)
      .then((d) => { if (live) setSt(d && d.ok && Array.isArray(d.reasons) ? { data: d } : { error: 'it answered no list' }); })
      .catch((e) => { if (live) setSt({ error: e?.body?.error || e?.body?.message || e.message || 'the read failed' }); });
    return () => { live = false; };
  }, [url, seq]);
  // After a dismissal or an undo: the list as read back (or, when that read failed, read again).
  const took = (d) => { if (d) setSt({ data: d }); else setSeq((n) => n + 1); };
  if (st.loading) return <p className="mt-2 text-[12px] text-text-muted">Reading the standing list…</p>;
  if (st.error) return <p className="mt-2 flex items-center gap-1 text-[12px] text-danger"><AlertTriangle size={12} /> The standing list could not be read: {st.error}</p>;
  const d = st.data;
  const dismissed = Array.isArray(d.dismissed) ? d.dismissed.filter((l) => l && l.dismissal) : [];
  return (
    <div className="mt-2 text-[12.5px] text-text-muted">
      <p>{d.heading || `Standing on ${d.today} (terms ended and not yet Expired in TRACON):`}</p>
      {d.dismissals_unread ? <p className="mt-1 flex items-center gap-1 text-warning" data-dismissals-unread><AlertTriangle size={12} /> What you marked Not needed could not be read, so every item is listed ({d.dismissals_unread}).</p> : null}
      {d.reasons.length ? (
        <ul className="mt-1 space-y-0.5">
          {d.reasons.map((r) => {
            const lines = (d.lines && Array.isArray(d.lines[r.reason]) ? d.lines[r.reason] : []).filter((l) => l && l.title);
            return (
              <li key={r.reason}>
                <span className="font-semibold text-text">{r.count}</span> {r.label}
                {lines.length ? (
                  <ul className="ml-4 mt-0.5 space-y-0.5">
                    {lines.slice(0, all[r.reason] ? lines.length : STANDING_LINES_SHOWN).map((l, i) => (
                      <li key={`${l.transaction_id || l.document_id || l.policy_id || (l.dismiss && l.dismiss.key) || i}|${l.intended_to || ''}`} className="flex flex-wrap items-baseline gap-x-2">
                        <span>{l.title}</span>
                        {l.deal_href ? <Link to={l.deal_href} className="font-medium text-accent underline-offset-2 hover:underline">{l.open_label}</Link> : null}
                        {l.dismiss ? (
                          <button type="button" className="font-medium text-text underline-offset-2 hover:underline" title={`Not needed: take ${l.title} off this list for good`} onClick={() => setAsking(l)} data-not-needed>
                            Not needed
                          </button>
                        ) : null}
                      </li>
                    ))}
                    {lines.length > STANDING_LINES_SHOWN ? (
                      <li>
                        <button type="button" className="font-medium text-accent underline-offset-2 hover:underline" onClick={() => setAll((a) => ({ ...a, [r.reason]: !a[r.reason] }))} data-show-all>
                          {all[r.reason] ? `Show the first ${STANDING_LINES_SHOWN} only` : `and ${lines.length - STANDING_LINES_SHOWN} more: show all ${lines.length}`}
                        </button>
                      </li>
                    ) : null}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : <p className="mt-1">Nothing standing.</p>}
      {dismissed.length ? (
        <div className="mt-1">
          <button type="button" className="font-medium text-accent underline-offset-2 hover:underline" aria-expanded={showDismissed} onClick={() => setShowDismissed((v) => !v)} data-show-dismissed>
            {dismissed.length} dismissed{showDismissed ? ' (hide)' : ' (show, with Undo)'}
          </button>
          {showDismissed ? <DismissedLines lines={dismissed} url={url} onDone={took} /> : null}
        </div>
      ) : null}
      {asking ? <NotNeededDialog line={asking} url={url} onClose={() => setAsking(null)} onDone={(x) => { setAsking(null); took(x); }} /> : null}
    </div>
  );
}

function SwitchRow({ s, state, onSaved }) {
  const [pick, setPick] = useState(state.mode);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPick(state.mode); }, [state.mode]);
  const unchanged = pick === state.mode && !state.why;

  async function save() {
    setSaving(true);
    try {
      const res = await api.patch('/api/tracon/admin/admin-settings', { updates: { [s.key]: pick } });
      if (res && res.errors && res.errors.length) throw new Error(res.errors[0].error || 'the switch was not written');
      let rows;
      try {
        rows = await readSwitches();
      } catch {
        toast.info(`${s.label}: saved, but the switch could not be read back. Reload to check it.`);
        onSaved();
        return;
      }
      const now = rows[s.key];
      if (now.mode === pick && !now.why) toast.success(`${s.label} is now ${labelOf(s, now.mode)}.`);
      else toast.error(`${s.label} reads ${labelOf(s, now.mode)}${now.why ? ` (${now.why})` : ''} after the save, not ${labelOf(s, pick)}.`);
      onSaved();
    } catch (e) {
      toast.error(e?.body?.error || e.message || 'Could not set the switch');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-card border border-border bg-surface p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm font-semibold text-text">{s.label}</span>
        <span className="text-[12px] text-text-muted">now {labelOf(s, state.mode)}{state.why ? ` (${state.why})` : ''}</span>
        <span className="ml-auto break-all font-mono text-[11px] text-text-dim">{s.key}</span>
      </div>
      <p className="mt-1 text-[12.5px] text-text-muted">{s.what}</p>
      <p className="mt-1 text-[12.5px] text-text-muted">{s.note}</p>
      {s.standing ? <StandingCounts url={s.standing} /> : null}
      <div className="mt-2.5 flex flex-wrap items-center gap-2" role="radiogroup" aria-label={`${s.label} switch`}>
        {s.modes.map((m) => (
          <button
            key={m.mode}
            type="button"
            role="radio"
            aria-checked={pick === m.mode}
            title={m.note}
            onClick={() => setPick(m.mode)}
            className={[
              'h-9 min-w-[5.5rem] rounded-card border px-3 text-sm',
              pick === m.mode ? 'border-accent bg-accent text-white' : 'border-border text-text hover:border-accent',
            ].join(' ')}
          >
            {m.label}
          </button>
        ))}
        <Button onClick={save} disabled={saving || unchanged} className="h-9">
          {saving ? 'Saving…' : unchanged ? 'Saved' : `Set ${labelOf(s, pick)}`}
        </Button>
      </div>
    </div>
  );
}

function SettingRow({ s, state, onSaved }) {
  const [text, setText] = useState(String(state.value));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(String(state.value)); }, [state.value]);
  const n = Number.parseInt(text, 10);
  const valid = /^\d+$/.test(text.trim()) && n >= s.min && n <= s.max;
  const unchanged = valid && n === state.value && !state.why;

  async function saveSetting() {
    setSaving(true);
    try {
      const years = Number.parseInt(text, 10);
      const res = await api.patch('/api/tracon/admin/admin-settings', { updates: { [s.key]: years } });
      if (res && res.errors && res.errors.length) throw new Error(res.errors[0].error || 'the setting was not written');
      let rows;
      try {
        rows = await readSettings();
      } catch {
        toast.info(`${s.label} ${years} ${unitFor(s, years)}: saved, but the setting could not be read back. Reload to check it.`);
        onSaved();
        return;
      }
      const now = rows[s.key];
      if (now.value === years && !now.why) toast.success(`${s.label} ${now.value} ${unitFor(s, now.value)}.`);
      else toast.error(`${s.label} reads ${now.value} ${unitFor(s, now.value)}${now.why ? ` (${now.why})` : ''} after the save, not ${years}.`);
      onSaved();
    } catch (e) {
      toast.error(e?.body?.error || e.message || 'Could not save the setting');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-card border border-border bg-surface p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm font-semibold text-text">{s.label} {state.value} {unitFor(s, state.value)}</span>
        {state.why ? <span className="text-[12px] text-text-muted">({state.why})</span> : null}
        <span className="ml-auto break-all font-mono text-[11px] text-text-dim">{s.key}</span>
      </div>
      <p className="mt-1 text-[12.5px] text-text-muted">{s.what}</p>
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-sm text-text">
          Every
          <input
            type="number"
            min={s.min}
            max={s.max}
            step={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label={`${s.label} (${s.min} to ${s.max} ${s.unit})`}
            className="h-9 w-20 rounded-card border border-border bg-bg px-2 text-sm text-text focus:border-accent focus:outline-none"
          />
          {unitFor(s, n)}
        </label>
        <Button onClick={saveSetting} disabled={saving || !valid || unchanged} className="h-9">
          {saving ? 'Saving…' : !valid ? `Enter ${s.min} to ${s.max}` : unchanged ? 'Saved' : `Set every ${n} ${unitFor(s, n)}`}
        </Button>
      </div>
    </div>
  );
}

export default function AutomationsAdmin() {
  const owner = ownerPagesVisible(useMe());
  const [rows, setRows] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [sw, st] = await Promise.all([readSwitches(), readSettings()]);
      setRows(sw);
      setSettings(st);
      setError('');
    } catch (e) {
      setError(e.message || 'Could not read the switches');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="text-lg font-semibold text-text">Automations</h1>
        <p className="mt-1 max-w-2xl text-[13px] text-text-muted">
          The switches of the scheduled and automatic sends, what each has standing for you, and the agency
          settings they read. "Not needed" takes an item off its list and the morning report for good (Undo is
          under "dismissed"). Off does nothing. A switch with no row, or one that cannot be read, reads as Off (the payment holds read
          as Notify). Automations not listed here have no switch on this page yet.
        </p>
      </div>
      {error ? (
        <p className="flex items-center gap-2 text-sm text-danger">
          <AlertTriangle size={14} /> {error}
        </p>
      ) : rows === null || settings === null ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : (
        <>
          <div className="space-y-2">
            {SWITCHES.filter((s) => !s.owner || owner).map((s) => (
              <SwitchRow key={s.key} s={s} state={rows[s.key]} onSaved={refresh} />
            ))}
          </div>
          {SETTINGS.some((s) => !s.owner || owner) ? (
            <div className="space-y-2">
              <h2 className="text-base font-semibold text-text">Agency settings</h2>
              {SETTINGS.filter((s) => !s.owner || owner).map((s) => (
                <SettingRow key={s.key} s={s} state={settings[s.key]} onSaved={refresh} />
              ))}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
